/**
 * v4.12.0 — Bulk Preview Modal.
 *
 * Shared confirmation surface for every bulk op (status · mode · platform ·
 * assignment · schedule). Loads a server-computed preview, renders an
 * impact summary + per-row before→after diff, then commits via apply.
 *
 * Safety contracts:
 *   - Operator must see the count of affected cards, locations affected,
 *     skipped/conflicted rows, and the literal before→after values.
 *   - Apply only enabled when there is a real diff to commit.
 *   - Audit logging happens server-side on apply.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowRight, X, Loader2, AlertTriangle, Info } from "lucide-react";
import api from "../../../lib/api";

const Badge = ({ children, tone = "neutral", testId }) => {
    const palette = {
        neutral: { bg: "rgba(148,163,184,0.12)", fg: "#cbd5f5" },
        change: { bg: "rgba(56,189,248,0.15)", fg: "#38bdf8" },
        skip: { bg: "rgba(148,163,184,0.10)", fg: "#94a3b8" },
        warn: { bg: "rgba(245,158,11,0.15)", fg: "#fbbf24" },
    }[tone];
    return (
        <span
            data-testid={testId}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
            style={{ background: palette.bg, color: palette.fg }}
        >
            {children}
        </span>
    );
};

const skipLabel = (reason, t) => {
    const map = {
        already_in_state: t("card_fleet.bulk.skip_already_in_state"),
        already_in_mode: t("card_fleet.bulk.skip_already_in_mode"),
        already_on_platform: t("card_fleet.bulk.skip_already_on_platform"),
        already_assigned: t("card_fleet.bulk.skip_already_assigned"),
        unknown_op: t("card_fleet.bulk.skip_unknown_op"),
    };
    return map[reason] || t("card_fleet.bulk.skip_no_change");
};

export default function BulkPreviewModal({
    open, op, payload, cardIds, scopeOrgId = '',
    onClose, onApplied,
}) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) {
            setPreview(null);
            setError(null);
            return;
        }
        let alive = true;
        setLoading(true);
        setError(null);
        api.post("/card-fleet/bulk/preview", {
            op, card_ids: cardIds, scope_organization_id: scopeOrgId || null, ...payload,
        })
            .then((r) => { if (alive) setPreview(r.data); })
            .catch((err) => {
                if (!alive) return;
                const detail = err?.response?.data?.detail;
                if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
                    setError(detail.errors.join("\n"));
                } else if (detail && typeof detail === "object" && detail.message) {
                    setError(detail.message);
                } else {
                    setError(typeof detail === "string" ? detail : t("card_fleet.bulk.apply_failed"));
                }
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, op, scopeOrgId, JSON.stringify(payload), JSON.stringify(cardIds)]);  // eslint-disable-line react-hooks/exhaustive-deps

    if (!open) return null;

    const totals = preview?.totals || {};
    const willChange = totals.will_change || 0;
    const locations = preview?.locations || [];
    const rows = preview?.rows || [];
    const adjustments = preview?.adjustments || [];

    const handleApply = async () => {
        if (applying || !willChange) return;
        setApplying(true);
        try {
            const res = await api.post("/card-fleet/bulk/apply", {
                op, card_ids: cardIds, scope_organization_id: scopeOrgId || null, ...payload,
            });
            const n = res.data?.updated_count ?? willChange;
            if (n === 0) {
                toast.info(t("card_fleet.bulk.applied_zero"));
            } else {
                toast.success(t("card_fleet.bulk.applied_success", { n }));
            }
            if (res.data?.adjustments?.length) {
                toast.info(t("card_fleet.bulk.auto_snap_toast", { n: res.data.adjustments.length }));
            }
            onApplied?.(res.data);
            onClose?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = (detail && typeof detail === "object" && Array.isArray(detail.errors))
                ? detail.errors.join("\n")
                : (detail && typeof detail === "object" && detail.message)
                    ? detail.message
                    : (typeof detail === "string" ? detail : t("card_fleet.bulk.apply_failed"));
            toast.error(msg);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
            data-testid="bulk-preview-modal"
        >
            <div
                className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
                onClick={onClose}
            />
            <div
                className="relative w-full max-w-3xl rounded-2xl border overflow-hidden flex flex-col max-h-[90vh]"
                data-insight-id="fleet.bulk.preview-modal"
                data-insight-label="Bulk preview"
                style={{
                    background: "rgba(8, 14, 28, 0.97)",
                    borderColor: "rgba(56, 189, 248, 0.30)",
                    boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)",
                }}
            >
                {/* Header */}
                <div
                    className="flex items-start justify-between px-5 py-4 border-b"
                    style={{ borderColor: "rgba(148, 163, 184, 0.12)" }}
                >
                    <div>
                        <h2
                            className="text-lg sm:text-xl font-semibold text-slate-100"
                            style={{ fontFamily: "Manrope, sans-serif" }}
                        >
                            {t("card_fleet.bulk.preview_title")}
                        </h2>
                        <p className="text-xs text-slate-400 mt-0.5">
                            {t("card_fleet.bulk.preview_subtitle")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="bulk-preview-close"
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Impact summary */}
                <div
                    className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4 border-b"
                    style={{ borderColor: "rgba(148, 163, 184, 0.10)" }}
                    data-testid="bulk-preview-totals"
                >
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                            {t("card_fleet.bulk.will_change")}
                        </div>
                        <div className="text-2xl font-semibold text-sky-300" data-testid="preview-will-change">
                            {loading ? "…" : willChange}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                            {t("card_fleet.bulk.skipped")}
                        </div>
                        <div className="text-2xl font-semibold text-slate-300" data-testid="preview-skipped">
                            {loading ? "…" : (totals.skipped ?? 0)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                            {t("card_fleet.bulk.locations_affected")}
                        </div>
                        <div className="text-2xl font-semibold text-emerald-300" data-testid="preview-locations">
                            {loading ? "…" : (totals.locations_affected ?? 0)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                            {t("card_fleet.bulk.out_of_scope")}
                        </div>
                        <div
                            className="text-2xl font-semibold"
                            style={{ color: (totals.out_of_scope || 0) ? "#f59e0b" : "#94a3b8" }}
                            data-testid="preview-out-of-scope"
                        >
                            {loading ? "…" : (totals.out_of_scope ?? 0)}
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0" data-testid="bulk-preview-body">
                    {loading && (
                        <div className="flex items-center justify-center py-12 text-sm text-slate-400 gap-2">
                            <Loader2 size={14} className="animate-spin" /> {t("common.loading")}
                        </div>
                    )}
                    {error && (
                        <div
                            className="rounded-lg border px-4 py-3 text-sm flex items-start gap-2"
                            style={{ borderColor: "rgba(248,113,113,0.30)", background: "rgba(248,113,113,0.08)" }}
                            data-testid="bulk-preview-error"
                        >
                            <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                            <pre className="whitespace-pre-wrap text-red-300/90 text-xs leading-relaxed font-sans">
                                {error}
                            </pre>
                        </div>
                    )}
                    {!loading && !error && adjustments.length > 0 && (
                        <div
                            className="rounded-lg border px-3 py-2 text-xs flex items-start gap-2 mb-3"
                            style={{ borderColor: "rgba(56,189,248,0.30)", background: "rgba(56,189,248,0.08)" }}
                            data-testid="bulk-preview-adjustments"
                        >
                            <Info size={12} className="text-sky-300 mt-0.5 flex-shrink-0" />
                            <span className="text-sky-200">
                                {t("card_fleet.bulk.auto_snap_toast", { n: adjustments.length })}
                            </span>
                        </div>
                    )}
                    {!loading && !error && rows.length > 0 && (
                        <>
                            {locations.length > 0 && (
                                <div className="mb-3 flex flex-wrap gap-1.5">
                                    {locations.map((l) => (
                                        <span
                                            key={l.id}
                                            className="text-[11px] px-2 py-0.5 rounded border text-slate-300"
                                            style={{
                                                background: "rgba(52,211,153,0.08)",
                                                borderColor: "rgba(52,211,153,0.25)",
                                            }}
                                        >
                                            {l.name}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
                                {t("card_fleet.bulk.preview_diff_label")}
                            </div>
                            <div className="rounded-lg border overflow-hidden"
                                 style={{ borderColor: "rgba(148, 163, 184, 0.10)" }}>
                                {rows.map((r) => (
                                    <div
                                        key={r.id}
                                        data-testid={`preview-row-${r.id}`}
                                        className="flex items-center gap-3 px-3 py-2 text-sm border-b last:border-b-0"
                                        style={{ borderColor: "rgba(148, 163, 184, 0.06)" }}
                                    >
                                        <div className="min-w-0 flex-[1.3]">
                                            <div className="text-slate-100 font-medium truncate">
                                                #{r.card_id || r.name || "—"}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {r.location_name || "—"}
                                            </div>
                                        </div>
                                        <div className="flex-[1.6] flex items-center gap-1 min-w-0">
                                            <code className="text-[11px] text-slate-400 truncate">{r.before}</code>
                                            <ArrowRight size={11} className="text-slate-500 flex-shrink-0" />
                                            <code className="text-[11px] text-sky-300 truncate">{r.after}</code>
                                        </div>
                                        <div className="flex-shrink-0">
                                            {r.will_change
                                                ? <Badge tone="change" testId="preview-row-change">{t("card_fleet.bulk.will_change")}</Badge>
                                                : <Badge tone="skip" testId="preview-row-skip">{skipLabel(r.skip_reason, t)}</Badge>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-end gap-2 px-5 py-3 border-t"
                    style={{ borderColor: "rgba(148, 163, 184, 0.12)" }}
                >
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="bulk-preview-cancel"
                        className="px-3 py-2 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5"
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={applying || loading || !willChange || !!error}
                        data-testid="bulk-preview-apply"
                        className="px-4 py-2 rounded-lg text-sm font-medium text-slate-900 bg-sky-400 hover:bg-sky-300 disabled:bg-slate-600 disabled:text-slate-400 transition-colors flex items-center gap-1.5"
                    >
                        {applying ? <Loader2 size={12} className="animate-spin" /> : null}
                        {applying
                            ? t("card_fleet.bulk.applying")
                            : (willChange === 1
                                ? t("card_fleet.bulk.apply_one")
                                : t("card_fleet.bulk.apply", { n: willChange }))
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}
